import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useReferral } from '../../../hooks/useReferral';
import { ReferralCodeRequest } from './referral.interface';
import { useDiscountAPI } from '../../../hooks/useDiscountAPI';

interface CreateDiscountFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
}

export const CreateReferralForm: React.FC<CreateDiscountFormProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { createReferralCode, loading } = useReferral();
  const { getDiscountCodes, loading: discountsLoading } = useDiscountAPI();

  const [discounts, setDiscounts] = useState<Array<{ id: string; code: string }>>([]);

  const [formData, setFormData] = useState<ReferralCodeRequest>({
    email: '',
    discountCodeId: '',
    commissionRate: 0,
    maxUsagePerUser: null,
    maxUsageTotal: null
  });

  const handleInputChange = <K extends keyof ReferralCodeRequest>(
    field: K,
    value: ReferralCodeRequest[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      discountCodeId: '',
      commissionRate: 0,
      maxUsagePerUser: null,
      maxUsageTotal: null
    });
  };

  useEffect(() => {
    const fetchDiscounts = async () => {
      const res = await getDiscountCodes({ size: 100, page: 0});
        setDiscounts(res.content.map((d: any) => ({ id: d.id, code: d.code })));
    };
      fetchDiscounts().then();
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email?.trim()) {
      toast.error('Please enter an email');
      return;
    }
    if (!formData.discountCodeId?.trim()) {
      toast.error('Please select a discount code');
      return;
    }
    if (formData.commissionRate === undefined || formData.commissionRate === null || Number(formData.commissionRate) <= 0) {
      toast.error('Commission rate must be greater than 0');
      return;
    }

    const payload: ReferralCodeRequest = {
      email: formData.email.trim(),
      discountCodeId: formData.discountCodeId.trim(),
      commissionRate: Number(formData.commissionRate),
      maxUsagePerUser: formData.maxUsagePerUser === null || formData.maxUsagePerUser === undefined || formData.maxUsagePerUser === ('' as any)
        ? null
        : Number(formData.maxUsagePerUser),
      maxUsageTotal: formData.maxUsageTotal === null || formData.maxUsageTotal === undefined || formData.maxUsageTotal === ('' as any)
        ? null
        : Number(formData.maxUsageTotal)
    };

    const res = await createReferralCode(payload);
    if (res) {
      toast.success('Referral code created successfully');
      if (onSubmit) onSubmit(res);
      handleClose();
    } else {
      toast.error('Failed to create referral code');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Referral Code
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <TextField
            label="Referrer Email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            type="email"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="discount-select-label">Discount Code</InputLabel>
            <Select
              labelId="discount-select-label"
              label="Discount Code"
              value={formData.discountCodeId}
              onChange={(e) => handleInputChange('discountCodeId', e.target.value as string)}
              disabled={discountsLoading}
              required
            >
              {discountsLoading && (
                <MenuItem value="" disabled>
                  Loading...
                </MenuItem>
              )}
              {!discountsLoading && discounts.length === 0 && (
                <MenuItem value="" disabled>
                  No discount codes found
                </MenuItem>
              )}
              {!discountsLoading &&
                discounts.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.code}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Commission Rate"
            fullWidth
            margin="normal"
            value={formData.commissionRate}
            onChange={(e) => handleInputChange('commissionRate', e.target.value === '' ? ('' as any) : parseFloat(e.target.value))}
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
            required
            helperText="Enter the commission rate as a number (e.g., 5 for 5%)"
          />
          <TextField
            label="Max Usage Per User (optional)"
            fullWidth
            margin="normal"
            value={formData.maxUsagePerUser ?? ''}
            onChange={(e) => handleInputChange('maxUsagePerUser', e.target.value === '' ? null : parseInt(e.target.value, 10))}
            type="number"
            inputProps={{ min: '0' }}
          />
          <TextField
            label="Max Usage Total (optional)"
            fullWidth
            margin="normal"
            value={formData.maxUsageTotal ?? ''}
            onChange={(e) => handleInputChange('maxUsageTotal', e.target.value === '' ? null : parseInt(e.target.value, 10))}
            type="number"
            inputProps={{ min: '0' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
